
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import { useMediaQuery, useTheme } from '@mui/material';

// Custom
import TenantContext from '@/context/TenantContext/TenantContext';
import ThemeContext from '@/context/ThemeContext/ThemeContext';

import MapLogo from '@/svgs/map_logo.svg?react';
import PrehnitLogo from '@/svgs/prehnit.svg?react';

type IHomePageFooterProps = {
  isAbsoluteBottom?: boolean;
  isBgColored?: boolean;
  // isForceSmall?: boolean;
};

const HomePageFooter = (props: IHomePageFooterProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));

  const themeContext = useContext(ThemeContext);
  const tenantContext = useContext(TenantContext);

  const { isAbsoluteBottom, isBgColored } = props;

  const isDarkTheme = themeContext?.theme === 'dark';

  const footerLogo = useMemo(() => {
    const hasSidebarGrb = !!tenantContext?.tenantTheme?.footer_logo;
    if (hasSidebarGrb) {
      return `data:${tenantContext?.tenantTheme?.footer_logo_type};base64, ${tenantContext?.tenantTheme?.footer_logo}`;
    }
    return undefined;
  }, [tenantContext]);

  const textVariant = smDown ? 'caption' : 'body2';
  const textColor = isBgColored ? 'white' : undefined;

  const mapLogo = <MapLogo height={60} width={smDown ? 160 : 170} title="MAP. logo" color={textColor} />;
  const prehnitLogo = <PrehnitLogo height={60} width={60} title="Prehnit logo" />;

  const footerLogoImg = footerLogo ? (
    <img
      src={footerLogo}
      style={{
        transition: 'width 0.3s',
        maxWidth: '225px',
        maxHeight: '75px',
      }}
      alt="Footer Logo"
    />
  ) : null;

  const geoPortalText = (
    <Stack direction="column">
      <Typography color={textColor} variant={textVariant}>
        MAP. GeoPortal
      </Typography>
      <Typography color={textColor} variant={textVariant}>
        Version: 2.1.5
      </Typography>
    </Stack>
  );

  const copyrightText = (
    <Stack direction="column" alignItems="flex-end">
      <Typography color={textColor} variant={textVariant}>
        © {new Date().getFullYear()} Prehnit
      </Typography>
      <Typography color={textColor} variant={textVariant}>
        {t('copyright')}
      </Typography>
    </Stack>
  );

  return (
    <Stack
      mt={2}
      bgcolor={isBgColored ? (isDarkTheme ? '#353535' : theme.palette.grey[800]) : undefined}
      sx={
        isAbsoluteBottom
          ? {
            position: 'absolute',
            width: '100%',
            bottom: 0,
            boxShadow: '0px -2px 4px -1px rgba(0,0,0,0.2), 0px -4px 5px 0px rgba(0,0,0,0.14), 0px -1px 10px 0px rgba(0,0,0,0.12);',
          }
          : undefined
      }
      borderTop={isBgColored ? `3px solid ${theme.palette.primary.main}` : undefined}
    >
      {isBgColored ? null : <Divider />}
      {smDown ? (
        // phones are too narrow for one row: logos on top, texts below, no tenant logo (it overflows the page)
        <Stack spacing={0.5} sx={{ p: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {mapLogo}
            {prehnitLogo}
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            {geoPortalText}
            {copyrightText}
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" spacing={1} sx={{ p: 1 }} justifyContent="start" alignItems="center">
          {mapLogo}
          {geoPortalText}
          <Box sx={{ flexGrow: 1 }} />
          {footerLogoImg}
          <Box sx={{ flexGrow: 1 }} />
          {copyrightText}
          {prehnitLogo}
        </Stack>
      )}
    </Stack>
  );
};

export default HomePageFooter;
